import { REGIONS_WITH_LOCALES, getDefaultLocaleForRegion } from "@client-shared/lib/constants";
import { getLocaleFromCountryCode } from "@client-shared/lib/shippingDocumentsUtils";
import type { Locale, PurchaseOrder, VendorRegion } from "@shared/types";

/**
 * Packing slip utilities for ShippingDocumentsDialog
 */

export interface PackingSlipConfigParams {
  translateGrindType: (value: string | undefined) => string | undefined;
  sltCustomerAccountNumber?: string;
  vendorName?: string;
}

export interface PackingSlipOptions {
  useDestinationLocale: boolean;
  vendorRegion?: string;
  vendorLocale: Locale;
  recipientLocale: Locale;
  translateGrindType: (value: string | undefined) => string | undefined;
  sltCustomerAccountNumber?: string;
  vendorName?: string;
}

const isRegionSupportedLocale = (vendorRegion: string | undefined, locale: Locale): boolean => {
  if (!vendorRegion) return false;
  const supported = REGIONS_WITH_LOCALES[vendorRegion as VendorRegion];
  return supported?.some((entry) => entry.locale === locale) ?? false;
};

/**
 * Gets packing slip options for an order. If the recipient locale is in the
 * vendor region's supported locales, it's reused as vendorLocale to collapse
 * bilingual headers — e.g. a Dutch roaster in the BDE region shipping to NL
 * renders single-language nl_NL instead of de_DE/nl_NL.
 */
export const getPackingSlipOptions = (order: PurchaseOrder, config: PackingSlipConfigParams): PackingSlipOptions => {
  const vendorRegion = order.vendorKey?.split(":")[0];
  const destCountry = order.shippingAddress?.countryCode ?? order.shippingAddress?.country ?? undefined;
  const recipientLocale: Locale = getLocaleFromCountryCode(destCountry ?? undefined);
  const regionDefaultLocale: Locale = (vendorRegion && getDefaultLocaleForRegion(vendorRegion)) || "en_US";
  const vendorLocale: Locale = isRegionSupportedLocale(vendorRegion, recipientLocale) ? recipientLocale : regionDefaultLocale;

  return {
    useDestinationLocale: true,
    vendorRegion,
    vendorLocale,
    recipientLocale,
    translateGrindType: config.translateGrindType,
    sltCustomerAccountNumber: config.sltCustomerAccountNumber,
    vendorName: config.vendorName,
  };
};
